import React from "react";
import {
    PictureAsPdf, Image, VideoFile, AudioFile, FolderZip, Description, TableChart, Slideshow,
    Code, InsertDriveFileOutlined,
} from "@mui/icons-material";

/**
 * The icon and colour a file of this type is drawn with.
 *
 * ONE GENERIC PAGE FOR EVERYTHING is what this replaces. A list of attachments is scanned, not read:
 * the reader is looking for "the PDF" or "the spreadsheet", and a column of identical grey pages
 * makes them read every filename to find it. The conventional glyph and colour -- red for PDF, green
 * for a spreadsheet, orange for slides -- is recognised before the name is.
 *
 * KEYED ON MIME TYPE FIRST, EXTENSION SECOND. The MIME type is what the server recorded and is
 * harder to fake with a rename, but it is frequently the useless application/octet-stream, and a
 * file named .xlsx is a spreadsheet whatever the browser guessed when it was uploaded.
 *
 * The colours are the ones people expect from desktop file managers rather than the app's palette.
 * That is deliberate: the point is instant recognition, and a house-styled PDF icon in company blue
 * would defeat it.
 */
type FileType = {icon: React.ElementType; color: string; label: string};

const PDF: FileType = {icon: PictureAsPdf, color: "#D93025", label: "PDF"};
const IMAGE: FileType = {icon: Image, color: "#7B4DFF", label: "Image"};
const VIDEO: FileType = {icon: VideoFile, color: "#C2185B", label: "Video"};
const AUDIO: FileType = {icon: AudioFile, color: "#00897B", label: "Audio"};
const ARCHIVE: FileType = {icon: FolderZip, color: "#C9820A", label: "Archive"};
const SHEET: FileType = {icon: TableChart, color: "#1E8E3E", label: "Spreadsheet"};
const SLIDES: FileType = {icon: Slideshow, color: "#E8710A", label: "Presentation"};
const DOC: FileType = {icon: Description, color: "#1A73E8", label: "Document"};
const SOURCE: FileType = {icon: Code, color: "#5F6368", label: "Code"};
const OTHER: FileType = {icon: InsertDriveFileOutlined, color: "#5F6368", label: "File"};

// Extension -> type. The long tail is deliberately absent: an unrecognised file gets the plain page,
// which is honest, where guessing from a three-letter extension would eventually be wrong out loud.
const BY_EXTENSION: Record<string, FileType> = {
    pdf: PDF,
    png: IMAGE, jpg: IMAGE, jpeg: IMAGE, gif: IMAGE, bmp: IMAGE, webp: IMAGE, svg: IMAGE, tif: IMAGE,
    tiff: IMAGE, heic: IMAGE,
    mp4: VIDEO, mov: VIDEO, avi: VIDEO, mkv: VIDEO, webm: VIDEO, wmv: VIDEO,
    mp3: AUDIO, wav: AUDIO, m4a: AUDIO, aac: AUDIO, flac: AUDIO, ogg: AUDIO,
    zip: ARCHIVE, gz: ARCHIVE, tar: ARCHIVE, rar: ARCHIVE, "7z": ARCHIVE, bz2: ARCHIVE,
    xls: SHEET, xlsx: SHEET, xlsm: SHEET, csv: SHEET, tsv: SHEET, ods: SHEET,
    ppt: SLIDES, pptx: SLIDES, odp: SLIDES,
    doc: DOC, docx: DOC, odt: DOC, rtf: DOC, txt: DOC, md: DOC,
    json: SOURCE, xml: SOURCE, yaml: SOURCE, yml: SOURCE, sql: SOURCE, js: SOURCE, ts: SOURCE,
    py: SOURCE, java: SOURCE, html: SOURCE, css: SOURCE,
};

function byMimeType(mimeType: string): FileType | null {
    const type = mimeType.toLowerCase().split(";")[0].trim();
    if (!type || type === "application/octet-stream") return null;

    if (type === "application/pdf") return PDF;
    if (type.startsWith("image/")) return IMAGE;
    if (type.startsWith("video/")) return VIDEO;
    if (type.startsWith("audio/")) return AUDIO;
    if (type.includes("spreadsheet") || type.includes("excel") || type === "text/csv") return SHEET;
    if (type.includes("presentation") || type.includes("powerpoint")) return SLIDES;
    if (type.includes("word") || type.includes("opendocument.text") || type === "text/plain") return DOC;
    if (type.includes("zip") || type.includes("compressed") || type.includes("tar")) return ARCHIVE;
    if (type.startsWith("text/") || type.includes("json") || type.includes("xml")) return SOURCE;
    return null;
}

export function fileTypeFor(name?: string | null, mimeType?: string | null): FileType {
    const fromMime = mimeType ? byMimeType(mimeType) : null;
    if (fromMime) return fromMime;

    // No dot, or a trailing dot, means no extension -- not an extension of "".
    const dot = (name ?? "").lastIndexOf(".");
    if (dot > 0 && dot < (name ?? "").length - 1) {
        const extension = (name ?? "").slice(dot + 1).toLowerCase();
        if (BY_EXTENSION[extension]) return BY_EXTENSION[extension];
    }
    return OTHER;
}
